import { AxiosResponse } from "axios";

import { API } from "../index";
import { BACKEND_HOST } from "../client";
import {
  PaginatedResponse,
  SiteBillingAdjustmentCreatePayload,
  SiteBillingApplyPreviewPayload,
  SiteBillingGeneratePreviewPayload,
  SiteBillingGenerateResponse,
  SiteBillingInvoice,
  SiteBillingInvoiceDetail,
  SiteBillingInvoiceFilters,
  SiteBillingMeStatus,
  SiteBillingPaymentCreatePayload,
  SiteBillingPolicyRevisionCreatePayload,
  SiteBillingSiteStatus,
  SiteBillingSiteStatusFilters,
  SiteBillingVisibilityResponse,
  SiteBillingVisibilityUpdatePayload,
} from "../types/site-billing";

const ADMIN_BASE = "/api/v2/site-billing/admin";
const ME_BASE = "/api/v2/site-billing/me";

const cleanParams = <T extends Record<string, unknown>>(params?: T): Partial<T> => {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== "" && value !== undefined && value !== null)
  ) as Partial<T>;
};

const buildQueryString = (params?: Record<string, unknown>) => {
  const cleaned = cleanParams(params);
  const query = new URLSearchParams();
  Object.entries(cleaned).forEach(([key, value]) => {
    query.set(key, String(value));
  });
  return query.toString();
};

export const siteBillingService = {
  getMyStatus: (): Promise<AxiosResponse<SiteBillingMeStatus>> => {
    return API.get(`${ME_BASE}/status/`);
  },

  getInvoices: (
    params?: SiteBillingInvoiceFilters
  ): Promise<AxiosResponse<PaginatedResponse<SiteBillingInvoice>>> => {
    return API.get(`${ADMIN_BASE}/invoices/`, { params: cleanParams(params) });
  },

  getInvoiceDetail: (invoiceId: number): Promise<AxiosResponse<SiteBillingInvoiceDetail>> => {
    return API.get(`${ADMIN_BASE}/invoices/${invoiceId}/`);
  },

  buildInvoiceExportUrl: (params?: SiteBillingInvoiceFilters): string => {
    const query = buildQueryString(params);
    return `${BACKEND_HOST}${ADMIN_BASE}/invoices/export.csv${query ? `?${query}` : ""}`;
  },

  generateInvoicesPreview: (
    payload: SiteBillingGeneratePreviewPayload
  ): Promise<AxiosResponse<SiteBillingGenerateResponse>> => {
    return API.post(`${ADMIN_BASE}/invoices/generate/`, {
      ...payload,
      apply: false,
    });
  },

  applyInvoicesPreview: (
    payload: SiteBillingApplyPreviewPayload
  ): Promise<AxiosResponse<SiteBillingGenerateResponse>> => {
    return API.post(`${ADMIN_BASE}/invoices/generate/`, {
      ...payload,
      anomaly_accept_site_ids: payload.anomaly_accept_site_ids ?? [],
      apply: true,
    });
  },

  createPayment: (invoiceId: number, payload: SiteBillingPaymentCreatePayload): Promise<AxiosResponse> => {
    return API.post(`${ADMIN_BASE}/invoices/${invoiceId}/approve-bank-transfer/`, payload);
  },

  createAdjustment: (invoiceId: number, payload: SiteBillingAdjustmentCreatePayload): Promise<AxiosResponse> => {
    return API.post(`${ADMIN_BASE}/invoices/${invoiceId}/adjustments/`, payload);
  },

  getSitesStatus: (
    params: SiteBillingSiteStatusFilters
  ): Promise<AxiosResponse<PaginatedResponse<SiteBillingSiteStatus>>> => {
    return API.get(`${ADMIN_BASE}/sites/status/`, { params: cleanParams(params) });
  },

  createPolicyRevision: (payload: SiteBillingPolicyRevisionCreatePayload): Promise<AxiosResponse> => {
    return API.post(`${ADMIN_BASE}/policies/`, payload);
  },

  getVisibility: (): Promise<AxiosResponse<SiteBillingVisibilityResponse>> => {
    return API.get(`${ADMIN_BASE}/visibility/`);
  },

  updateVisibility: (payload: SiteBillingVisibilityUpdatePayload): Promise<AxiosResponse> => {
    return API.put(`${ADMIN_BASE}/visibility/`, payload);
  },
};
